import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import 'mathlive/static.css';

/* ───────── pages & layouts ───────── */
import LogInPage from './pages/LogInPage.tsx';
import SetPasswordPage from './pages/SetPasswordPage.tsx';
import ProfilePage from './pages/ProfilePage.tsx';
import TestsPage from './pages/TestsPage.tsx';
// TODO: Re-enable when scheduling/timetable feature is ready
// import OfficeHoursPage from './pages/SchedulePage/OfficeHoursPage.tsx';
import DashboardLayout from './Dashboard/DashboardLayout.tsx';
import PracticePage from './pages/PracticePage/PracticePage.tsx';
import LessonsPage from './pages/LessonsPage.tsx';
import AssignedLessonsPage from './pages/AssignedLessonsPage.tsx';
import LessonReaderPage from './pages/LessonReaderPage.tsx';

/* ───────── auth / role guards ─────── */
import AuthRedirect from './AuthRedirect.tsx'; // redirects authed → /practice
import ProtectedRoute from './ProtectedRoute.tsx'; // checks Supabase session
import RequireStudentOrTutorSession from './RequireStudentOrTutorSession.tsx';
import RequireCompanyStudent from './RequireCompanyStudent.tsx';

import './App.css';
import TestPage from './pages/TestPage/TestPage.tsx';
import AuthHandoff from './pages/AuthHandoff.tsx';
import TutorSessionEntry from './pages/TutorSessionEntry.tsx';
// TODO: Re-enable when scheduling/timetable feature is ready
// import RequestOneOnOnePage from './pages/SchedulePage/RequestOneOnOne.tsx';
import SavedQuestionsPage from './pages/SavedQuestionsPage.tsx';
import MarkDisputesPage from './pages/MarkDisputesPage.tsx';
import SignUpPage from './pages/SignUpPage.tsx';
import CompleteSignupPage from './pages/CompleteSignupPage.tsx';
import WelcomePage from './pages/WelcomePage.tsx';
import OAuthCodeGate from './OAuthCodeGate.tsx';
import { TutorSessionProvider } from './context/TutorSessionContext.tsx';

const App: React.FC = () => (
  <BrowserRouter>
    <TutorSessionProvider>
      <OAuthCodeGate>
      <Routes>
      {/* ---------- PUBLIC ---------- */}
      <Route path="/auth-handoff" element={<AuthHandoff />} />
      <Route path="/reset-password" element={<SetPasswordPage />} />
      {/* Entry point for the whole suite: everyone signs in here, and non-student
          roles are handed off to the admin/parent app from the login page. */}
      <Route
        path="/login"
        element={
          <AuthRedirect>
            <LogInPage />
          </AuthRedirect>
        }
      />
      <Route
        path="/signup"
        element={
          <AuthRedirect>
            <SignUpPage />
          </AuthRedirect>
        }
      />
      {/* Where the signup confirmation email and Google OAuth both land.
          Deliberately unguarded: the student record may not exist yet. */}
      <Route path="/welcome" element={<WelcomePage />} />
      {/* Post-Google: session exists, student row does not, until year/subjects. */}
      <Route path="/signup/complete" element={<CompleteSignupPage />} />

      <Route path="/" element={<Navigate to="/practice" replace />} />

      {/* ---------- STUDENT-PROTECTED (inside dashboard shell) ---------- */}
      <Route element={<DashboardLayout />}>
        <Route
          path="/profile"
          element={
            <RequireStudentOrTutorSession>
              <ProtectedRoute element={<ProfilePage />} />
            </RequireStudentOrTutorSession>
          }
        />

        {/* Disputes are resolved by company tutors, so public students have no
            queue to send them to. */}
        <Route
          path="/practice/mark-disputes"
          element={
            <RequireStudentOrTutorSession>
              <RequireCompanyStudent>
                <ProtectedRoute element={<MarkDisputesPage />} />
              </RequireCompanyStudent>
            </RequireStudentOrTutorSession>
          }
        />

        <Route
          path="/practice/saved-questions"
          element={
            <RequireStudentOrTutorSession>
              <ProtectedRoute element={<SavedQuestionsPage />} />
            </RequireStudentOrTutorSession>
          }
        />

        <Route
          path="/tests"
          element={
            <RequireStudentOrTutorSession>
              <ProtectedRoute element={<TestsPage />} />
            </RequireStudentOrTutorSession>
          }
        />

        {/* Practice is the landing spot for signed-out visitors, so it renders
            without a session (Supabase RLS still gates any data it asks for). */}
        <Route path="/practice" element={<PracticePage />} />

        {/* Lessons are company content, so public self-signup students don't get them. */}
        <Route
          path="/lessons"
          element={
            <RequireStudentOrTutorSession>
              <RequireCompanyStudent>
                <ProtectedRoute element={<LessonsPage />} />
              </RequireCompanyStudent>
            </RequireStudentOrTutorSession>
          }
        />
        <Route
          path="/lessons/assigned"
          element={
            <RequireStudentOrTutorSession>
              <RequireCompanyStudent>
                <ProtectedRoute element={<AssignedLessonsPage />} />
              </RequireCompanyStudent>
            </RequireStudentOrTutorSession>
          }
        />

        {/* TODO: Re-enable when scheduling/timetable feature is ready */}
        {/* <Route
          path="/timetable"
          element={
            <RequireStudent>
              <OfficeHoursPage />
            </RequireStudent>
          }
        />

        <Route
          path="/timetable/request"
          element={
            <RequireStudent>
              <RequestOneOnOnePage />
            </RequireStudent>
          }
        /> */}

        <Route path="*" element={<Navigate to="/practice" replace />} />
      </Route>

      {/* ---------- stand-alone test-taking & review routes ---------- */}
      {/* Review a specific past attempt */}
      <Route
        path="/tests/:testId/result/:resultId"
        element={
          <RequireStudentOrTutorSession>
            <ProtectedRoute element={<TestPage />} />
          </RequireStudentOrTutorSession>
        }
      />
      <Route
        path="/tests/:testId/result/:resultId/mark"
        element={
          <RequireStudentOrTutorSession>
            <ProtectedRoute element={<TestPage />} />
          </RequireStudentOrTutorSession>
        }
      />

      {/* Take a test now */}
      <Route
        path="/tests/:testId"
        element={
          <RequireStudentOrTutorSession>
            <ProtectedRoute element={<TestPage />} />
          </RequireStudentOrTutorSession>
        }
      />

      {/* Standalone lesson reader */}
      <Route
        path="/lessons/:lessonId"
        element={
          <RequireStudentOrTutorSession>
            <RequireCompanyStudent>
              <ProtectedRoute element={<LessonReaderPage />} />
            </RequireCompanyStudent>
          </RequireStudentOrTutorSession>
        }
      />

      <Route
        path="/tutor-session/:sessionId"
        element={<ProtectedRoute element={<TutorSessionEntry />} />}
      />
      </Routes>
      </OAuthCodeGate>
    </TutorSessionProvider>
  </BrowserRouter>
);

export default App;
